

import { Component, Vue, Watch, Prop } from "vue-property-decorator"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"
import { IntegrationColumn, IntegrationEventMapping, IntegrationOut, IntegrationSettings, OutConfiguration, OutSettings } from "@/types/integration"
import DataForm from "../DataForm.vue"
import DefinitionTitle from "@/components/Config/Definitions/DefinitionTitle.vue"

import { Field, Team, Template } from "@/types"
import { CaseColumn } from "@/types/caseColumns"
import Guid from "@/scripts/guid"

import FieldMapping from "./FieldMapping.vue"
import TeamPermissionsSelector from "../Permissions/TeamPermissionsSelector.vue"

interface OutputOption {
	key: string
	label: string
	outputType: string | null
	description: string | null
}

@Component({
	components: {
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"data-form": DataForm
	}
})
export default class OutputConfiguration extends Vue {
	@Prop()
	value!: { [key: string]: OutConfiguration }

	@Prop()
	integrationOut!: IntegrationOut | null

	@Prop({ default: () => [] })
	teams!: Team[]

	@Prop({ default: () => [] })
	fields!: IntegrationColumn[];

	@Prop({ default: () => [] })
	casesFields!: Field[];

	@Prop({ default: () => [] })
	caseColumns!: CaseColumn[]

	private selectedOption: OutputOption | null = null

	mounted () {
		if (!this.value) {
			this.$emit("input", {})
		}

		if (this.integrationOut) {
			const options = this.outputOptions
			this.selectedOption = options.length > 0 ? options[0] : null
		}
	}

	get outputOptions (): OutputOption[] {
		if (!this.integrationOut || !this.value || !this.integrationOut.out) {
			return []
		}

		return Object.keys(this.value).map(key => {
			return {
				key,
				label: this.value[key].title,
				outputType: this.value[key].outputType,
				description: this.integrationOut!.out.findAndGet(output => output.key === this.value[key].outputType, outputType => outputType.title, null)
			}
		})
	}

	get selectedConfiguration () : OutConfiguration | null {
		if (!this.selectedOption || !this.integrationOut) return null
		return this.value[this.selectedOption.key]
	}

	get selectedOutputType (): OutSettings | null {
		if (!this.selectedConfiguration || !this.integrationOut || !this.selectedConfiguration.outputType) return null
		return this.integrationOut.out.find(option => option.key === this.selectedConfiguration!.outputType)!
	}

	get selectedOutputConfigurationColumns (): IntegrationColumn[] {
		return this.selectedOutputType && this.selectedOutputType.setupColumns
			? this.selectedOutputType.setupColumns
			: []
	}

	get configFields () {
		return this.caseColumns
			? this.caseColumns.map(column => {
				return {
					field: {
						id: column.key,
						title: column.label
					}
				}
			})
			: []
	}

	get configColumns (): any[] {
		if (!this.integrationOut) return []

		return [
			{
				key: "title",
				label: "Title",
				dataType: "text",
				dynamicField: false
			},
			{
				key: "outputType",
				valueColumn: "outputType",
				label: "Type",
				dataType: "list",
				dynamicField: false,
				options: this.integrationOut!.out.map(o => {
					return {
						id: o.key,
						title: o.title
					}
				})
			},
			{
				key: "onDemandPermissions",
				label: "On Demand Sending",
				dataType: "list",
				dynamicComponent: TeamPermissionsSelector,
				componentProperties: {
					placeholder: "Anyone can send on demand",
					teams: this.teams
				}
			},
			{
				key: "outFieldMapping", // Porperty value maps to Out field mapping
				label: "Field Mapping",
				dataType: "list",
				dynamicComponent: FieldMapping,
				inModal: () => { return this.integrationOut!.supportOutFieldMapping },
				componentProperties: {
					fields: this.fields, // Property on output configuration, pull from integration
					caseColumns: this.caseColumns,
					allowCreateFields: false
				}
			},
			...(
				this.selectedOutputType && this.selectedOutputType.supportsTrackingChanges
					? [
						{
							key: "trackChanges",
							label: "Track any additional changes made",
							dataType: "checkbox"
						}
					]
					: []
			),
			...this.selectedOutputConfigurationColumns.map(column => {
				if (column.dataType === "format-fields") {
					return {
						key: "configuration." + column.key,
						label: column.title,
						dataType: column.dataType,
						dynamicComponent: DefinitionTitle,
						componentProperties: {
							fields: this.configFields,
							description: null,
							label: null
						}
					}
				}

				return {
					key: "configuration." + column.key,
					label: column.title,
					dataType: column.dataType
				} as CaseColumn
			})
		]
	}

	createNew (search: string | null) {
		const key = Guid.newGuid()

		this.$emit("input", {
			...this.value,
			[key]: Vue.observable({
				key,
				enabled: true,
				title: search && search.length > 0 ? search : "New",
				outputType: null,
				outFieldMapping: {},
				configuration: {},
				onDemandPermissions: null
			} as OutConfiguration)
		})

		Vue.nextTick().then(() => {
			const newOption = this.outputOptions.find(o => o.key === key)
			if (newOption) {
				this.selectedOption = newOption
			}
		})
	}
}

